import React, { useState } from 'react';
import { X, ArrowRight } from 'lucide-react';
import type { Account } from './AccountOverview';

type Props = {
  accounts: Account[];
  onTransfer: (fromId: Account['id'], toId: Account['id'], amount: number) => void;
  onClose: () => void;
};

export default function TransferModal({ accounts, onTransfer, onClose }: Props) {
  const [fromAccount, setFromAccount] = useState<Account['id']>('general');
  const [toAccount, setToAccount] = useState<Account['id']>('savings');
  const [amount, setAmount] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const transferAmount = Number(amount);
    const from = accounts.find(a => a.id === fromAccount);
    
    if (!from) {
      setError('Please select source account');
      return;
    }
    
    if (transferAmount <= 0) {
      setError('Please enter a valid amount');
      return;
    }
    
    if (transferAmount > from.balance) {
      setError('Insufficient balance');
      return;
    }
    
    if (fromAccount === toAccount) {
      setError('Cannot transfer to the same account');
      return;
    }

    onTransfer(fromAccount, toAccount, transferAmount);
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 grid place-items-center p-4">
      <div className="bg-white/90 backdrop-blur-md p-8 rounded-2xl shadow-xl w-full max-w-lg">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-semibold text-gray-800">Transfer Points</h3>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100/80 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-[1fr,auto,1fr] gap-4 items-center">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                From Account
              </label>
              <select
                value={fromAccount}
                onChange={(e) => {
                  setFromAccount(e.target.value as Account['id']);
                  setError('');
                }}
                className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
              >
                {accounts.map(account => (
                  <option key={account.id} value={account.id}>
                    {account.name} ({account.balance} pts)
                  </option>
                ))}
              </select>
            </div>
            
            <div className="flex items-center justify-center h-full pt-6">
              <ArrowRight className="w-5 h-5 text-gray-400" />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                To Account
              </label>
              <select
                value={toAccount}
                onChange={(e) => {
                  setToAccount(e.target.value as Account['id']);
                  setError('');
                }}
                className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
              >
                {accounts.map(account => (
                  <option key={account.id} value={account.id}>
                    {account.name} ({account.balance} pts)
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Amount (pts)
            </label>
            <input
              type="number"
              value={amount}
              onChange={(e) => {
                setAmount(e.target.value);
                setError('');
              }}
              min="0"
              step="1"
              className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
              placeholder="Enter amount to transfer"
            />
          </div>

          {error && (
            <div className="text-red-500 text-sm text-center">
              {error}
            </div>
          )}

          <button
            type="submit"
            className="w-full bg-gray-800/90 text-white py-3 px-4 rounded-lg hover:bg-gray-900 transition-all backdrop-blur-sm flex items-center justify-center gap-2 text-sm font-medium"
            disabled={!amount || Number(amount) <= 0}
          >
            Transfer Points
          </button>
        </form>
      </div>
    </div>
  );
}